import { mergeAttributes, Node } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

import { enterMovesToNextLine } from "./line-navigation"
import {
	parseUpdateTags,
	serializeUpdateTags,
	updateAnchor,
} from "./update-html"

// Above the paragraph's own `p` rule, which would claim every line.
const PARSE_PRIORITY = 1000

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		update: {
			insertUpdate: (label?: string) => ReturnType
		}
	}
}

export const UpdateLabelExtension = Node.create({
	name: "updateLabel",
	content: "text*",
	marks: "",
	defining: true,

	parseHTML() {
		return [{ tag: "p[data-update-label]", priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"p",
			mergeAttributes(HTMLAttributes, {
				"data-update-label": "",
				class: "pl-update-label",
			}),
			0,
		]
	},
})

export const UpdateDescriptionExtension = Node.create({
	name: "updateDescription",
	content: "text*",
	marks: "",
	defining: true,

	parseHTML() {
		return [{ tag: "p[data-update-description]", priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"p",
			mergeAttributes(HTMLAttributes, {
				"data-update-description": "",
				class: "pl-update-description",
			}),
			0,
		]
	},
})

// Label and description share one column, so the pair sticks as one.
export const UpdateMetaExtension = Node.create({
	name: "updateMeta",
	content: "updateLabel updateDescription",
	defining: true,

	parseHTML() {
		return [{ tag: 'div[data-type="update-meta"]', priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "update-meta",
				class: "pl-update-meta",
			}),
			0,
		]
	},
})

export const UpdateContentExtension = Node.create({
	name: "updateContent",
	content: "block+",
	defining: true,

	parseHTML() {
		return [
			{ tag: 'div[data-type="update-content"]', priority: PARSE_PRIORITY },
		]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "update-content",
				class: "pl-update-content",
			}),
			0,
		]
	},
})

export const UpdateExtension = Node.create({
	name: "update",
	// Its keys run before the core Enter chain, which tries to split the line.
	priority: 200,
	group: "block",
	content: "updateMeta updateContent",
	defining: true,
	isolating: true,

	addAttributes() {
		return {
			// Mintlify shows tags as filters beside the changelog, not in the entry.
			tags: {
				default: [] as string[],
				parseHTML: (element: HTMLElement) =>
					parseUpdateTags(element.getAttribute("data-tags")),
				renderHTML: (attributes: Record<string, unknown>) => {
					const tags = Array.isArray(attributes.tags)
						? attributes.tags.filter(
								(tag): tag is string => typeof tag === "string",
							)
						: []
					const serialized = serializeUpdateTags(tags)
					return serialized ? { "data-tags": serialized } : {}
				},
			},
		}
	},

	parseHTML() {
		return [{ tag: 'div[data-type="update"]', priority: PARSE_PRIORITY }]
	},

	renderHTML({ node, HTMLAttributes }) {
		const label = node.firstChild?.firstChild?.textContent ?? ""
		const anchor = updateAnchor(label)
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "update",
				class: "pl-update",
				...(anchor ? { id: anchor } : {}),
			}),
			0,
		]
	},

	addKeyboardShortcuts() {
		return {
			Enter: () =>
				enterMovesToNextLine(this.editor, ["updateLabel", "updateDescription"]),
		}
	},

	addCommands() {
		return {
			insertUpdate:
				(label = "") =>
				({ chain, state }) => {
					const from = state.selection.from
					return chain()
						.insertContent({
							type: this.name,
							content: [
								{
									type: "updateMeta",
									content: [
										label
											? {
													type: "updateLabel",
													content: [{ type: "text", text: label }],
												}
											: { type: "updateLabel" },
										{ type: "updateDescription" },
									],
								},
								{ type: "updateContent", content: [{ type: "paragraph" }] },
							],
						})
						.command(({ tr, dispatch }) => {
							if (!dispatch) return true
							let target = -1
							tr.doc.descendants((node, pos) => {
								if (target >= 0) return false
								if (node.type.name === "updateLabel" && pos >= from - 1) {
									target = pos + node.nodeSize - 1
									return false
								}
								return true
							})
							if (target >= 0) {
								tr.setSelection(TextSelection.near(tr.doc.resolve(target)))
							}
							return true
						})
						.run()
				},
		}
	},
})

export const UpdateExtensions = [
	UpdateExtension,
	UpdateMetaExtension,
	UpdateLabelExtension,
	UpdateDescriptionExtension,
	UpdateContentExtension,
]
